import React, {useCallback} from 'react';
import SwivuModal from "./SwivuModal.jsx";
import {WIDGET_KEYS} from "../../../other/constants/WidgetKeys.jsx";
import {defaultAddDeleteButton, generateMultiFilterConfigFromModel, getDivider} from "../../../other/utils/utils.jsx";
import {TAGS_KEYS} from "../../../other/constants/TagsKeys.jsx";
import {STORE_KEYS} from "../../../other/constants/StoreKeys.jsx";
import EditorBase from "../../../bryntum-lib/editors/base/EditorBase.jsx";
import {PERSISTENCE_STATE_KEYS} from "../../../other/constants/PersistenceStateKeys.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";
import { EDIT_MESSAGES, SETTING_TAB_WIDGET_KEYS } from "../../../other/constants/OtherKeys.jsx";
import {Contact} from "../../../bryntum-lib/models/Contact.jsx";
import {StringHelper} from "@bryntum/schedulerpro";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";

class SwivuContactFinder extends EditorBase {
  static get $name() {
    return "SwivuContactFinder";
  }

  static get type() {
    return WIDGET_KEYS.swivuContactFinderPopup;
  }

  static get configurable() {
    return {
      title: "Contact Editor",
      autoClose: false,
      draggable: true,
      modal: true,
      closable: true,
      stateId: PERSISTENCE_STATE_KEYS.contactEditor,
      minWidth: "25em",
      minHeight: "41em",
      width: "58em",
      height: "800px",
      maxWidth: "100vw",
      maxHeight: "100vh",
      scrollable: {
        overflowX: "hidden",
        overflowY: "auto"
      },
      header:{
        cls: 'h-[50px]',
      },
      items: {
        contactContainer: {
          type: "container",
          autoUpdateRecord: true,
          defaults: {
            labelWidth: "7em",
          },
          contentElementCls: "gap-[1em] @container",
          items: {
            searchField: {
              label: "Search",
              type: "combo",
              displayField: "name",
              valueField: "id",
              filterOperator: "*",
              onChange: 'up.onSearchContactSelected',
              cls: "flex-[1_0_100%]",
              placeholder: "Does the contact already exist?",
              clearable: true,
            },
            divider: getDivider({ text: "", cls: "m-0 mb-[0.1em]" }),
            firstRow: {
              type: "container",
              cls: "@flex-[1_0_100%] @md:flex-[1_0_35%]",
              autoUpdateRecord: true,
              defaults: {
                labelWidth: "7em",
              },
              items: {
                nameField: {
                  type: "text",
                  label: "Name",
                  name: "name",
                  onInput: "up.onNameChange",
                },
                phoneField: {
                  type: WIDGET_KEYS.fieldPhone,
                  label: "Phone",
                  name: "phone",
                },
                emailField: {
                  type: WIDGET_KEYS.fieldEmail,
                  label: "Email",
                  name: "email",
                },
              },
            },
            secondRow: {
              type: "container",
              cls: "@flex-[1_0_100%] @md:flex-[1_0_35%]",
              autoUpdateRecord: true,
              defaults: {
                labelWidth: "7em",
              },
              items: {
                statusField: {
                  name: "statusId",
                  valueField: "id",
                  displayField: "tagValue",
                  label: "Status",
                  text: "Status",
                  clearable: true,
                  type: WIDGET_KEYS.settingsCombo,
                  editMessage: EDIT_MESSAGES.status,
                  tabName: SETTING_TAB_WIDGET_KEYS.contactStatusTab,
                  onSwivuEditMessage: "onClickSwivuEditMessage"
                },
                categoryField: {
                  name: "categoryId",
                  valueField: "id",
                  displayField: "tagValue",
                  label: "Category",
                  text: "Category",
                  type: WIDGET_KEYS.settingsCombo,
                  clearable: true,
                  editMessage: EDIT_MESSAGES.category,
                  tabName: SETTING_TAB_WIDGET_KEYS.contactCategoryTab,
                  onSwivuEditMessage: "onClickSwivuEditMessage"
                },
                tagsField: {
                  name: "tagsId",
                  valueField: "id",
                  displayField: "tagValue",
                  label: "Tags",
                  text: "Tags",
                  multiSelect: true,
                  maxHeight: '45px',
                  type: WIDGET_KEYS.settingsCombo,
                  clearable: true,
                  editMessage: EDIT_MESSAGES.tag,
                  tabName: SETTING_TAB_WIDGET_KEYS.contactTagsTab,
                  onSwivuEditMessage: "onClickSwivuEditMessage"
                },
              }
            },
            addressFieldFull: {
              type: "text",
              label: "Address",
              name: "address",
            },
            associatedEntitiesField: {
              type: "combo",
              multiSelect: true,
              hideTrigger: true,
              label: "Linked To",
              text: "Linked To",
              chipView: {
                listeners: {
                  item: "up.onClickLinkedEntity"
                }
              },
              disabled: false,
              maxHeight: '120px',
              valueField: "id",
              displayField: "name",
              onChange: "up.onAssociatedEntitiesChange",
              triggers: {
                entityFinder: {
                  cls: "b-fa b-fa-add",
                  handler: "up.onAssociatedEntitiesTrigger"
                }
              }
            },
            notesField: {
              type: "textarea",
              label: "Notes",
              height: "6em",
              name: "noteText",
              cls: "mb-1"
            },
          }
        },
      },
      bbar: {
        items: {
          ...defaultAddDeleteButton({
            addButton: {onClick: "up.onAddContact", text: "Save Contact", color: "b-green", icon: "", disabled: true},
          }),
        }
      }
    }
  }

  loadRecord(record, context, otherParams) {
    super.editRecord(record, context, otherParams);
    this.otherParams = otherParams;
    this.context = context;

    const {
      searchField, associatedEntitiesField, contactContainer,
      statusField, categoryField, tagsField,
    } = this.widgetMap;

    // Opening Entity Finder triggers a reload which clears field
    // we don't to input to be typeable
    associatedEntitiesField.input.disabled = true;

    const contactTagValueStore = this.context.project.getCrudStore(STORE_KEYS.contactTagValuesStore);
    statusField.store.data = contactTagValueStore.query((s) => s.tagSet === TAGS_KEYS.status, true);
    categoryField.store.data = contactTagValueStore.query((s) => s.tagSet === TAGS_KEYS.category, true);
    tagsField.store.data = contactTagValueStore.query((s) => s.tagSet === TAGS_KEYS.tag, true);
    searchField.store.data = context.project.contactsStore.query(() => true, true).reduce((previousRecords, contactRecord) => {
      if (contactRecord.clientContacts.length) {
        const clientContactData = contactRecord.clientContacts.map((clientContact) => {
          const id = StringHelper.generateUUID()
          return {
            id,
            name: `${clientContact.clientRecord.name} | ${contactRecord.name}`,
            clientId: clientContact.clientRecord.id,
            contactId: contactRecord.id
          };
        })
        return  [...previousRecords, ...clientContactData]
      } else {
        const id = StringHelper.generateUUID()
        return [
          ...previousRecords,
          // Defining `clientId` key because if first record does not have `clientId` it won't be mapped to combo's store
          { id, name: contactRecord.name, contactId: contactRecord.id, clientId: null }];
      }
    }, [])
    if (otherParams.isFirstShow) {
      contactContainer.record = record;
      // When editor is opened from contact column tooltip
      if (!record.isPhantom) {
        let contactRecord = null;
        if (otherParams.clientRecord) {
          contactRecord = searchField.items.find((item) => item.clientId === otherParams.clientRecord.id && item.contactId === record.id);
        } else {
          contactRecord = searchField.items.find(item => item.contactId === record.id);
        }
        searchField.value = contactRecord.id;
      }
      // The `added` refers to new entities that are being associated with a contact record. These entities are not yet saved.
      // The `removed` refers to entities that were previously associated with a contact record but are now being disassociated.
      this.linkedEntitiesData = { added: [], removed: [] };
      if (otherParams.selectedEntities) {
        this.onEntitiesSelected(otherParams.selectedEntities)
      }
    }
  }

  onClickSwivuEditMessage({ tabName }) {
    this.context.editorHandler.openEditor(EDITOR_KEYS.settingGeneralEditor, null, { selectTabByTypeOnOpen: tabName }, { by: "recordType", useExisting: true });
  }

  onSearchContactSelected({ value }) {
    const { searchField, contactContainer, addButton, associatedEntitiesField } = this.widgetMap;
    this.linkedEntitiesData = { added: [], removed: [] };
    if (value) {
      // we need to populate the combo before combo can display entities.
      this.selectedContactRecord = this.context.project.contactsStore.getById(searchField.selected.contactId);
      contactContainer.record = this.selectedContactRecord.copy();
      const linkedEntities = this.selectedContactRecord.entities || [];
      associatedEntitiesField.items = linkedEntities;
      associatedEntitiesField.value = linkedEntities.map((entity) => entity.id);
      addButton.disabled = false;
    } else {
      this.selectedContactRecord = null;
      contactContainer.record = this.context.project.contactsStore.createRecord({});
      associatedEntitiesField.items = [];
      associatedEntitiesField.value = [];
      addButton.disabled = true;
    }
    if (this.otherParams.selectedEntities) {
      this.onEntitiesSelected(this.otherParams.selectedEntities)
    }
  }

  onAssociatedEntitiesChange({ source, userAction, oldValue, value }) {
    if (userAction) {
      const recordsToRemove = oldValue.filter((val) => !value.find(
        (newVal) => newVal === val)
      )
      source.items = source.items.filter((record) => {
        if (recordsToRemove.includes(record.id)) {
          const isNewLinkedEntity = this.linkedEntitiesData.added.find((linkedEntity) => linkedEntity.id === record.id);
          if (!isNewLinkedEntity) {
            this.linkedEntitiesData.removed.push(record)
          } else {
            this.linkedEntitiesData = {
              ...this.linkedEntitiesData,
              added: this.linkedEntitiesData.added.filter((linkedEntity) => linkedEntity.id !== record.id)
            }
          }
          return false;
        }
        return true;
      })
    }
  }


  onAddContact() {
    const { contactContainer } = this.widgetMap;
    const contactRecord = contactContainer.record;
    this.context.project.beginBatch();
    if (this.selectedContactRecord) {
      Object.entries(generateMultiFilterConfigFromModel(Contact)).forEach(([field, { persist, internal }]) => {
        if (!internal && persist) {
          this.selectedContactRecord[field] = contactRecord[field];
        }
      })
      this.linkedEntitiesData.removed.forEach((linkedEntity) => {
        this.context.project.getCrudStore(linkedEntity.entityContactStoreKey).find((entityContact) => (
          entityContact.contactId === this.selectedContactRecord.id &&
          entityContact[linkedEntity.entityRelationKey] === linkedEntity.entityRecord.id
        )).remove();
      })
    } else {
      this.context.project.contactsStore.add(contactRecord);
    }
    this.linkedEntitiesData.added.forEach((linkedEntity) => {
      this.context.project.getCrudStore(linkedEntity.entityContactStoreKey).add({
        contactId: this.selectedContactRecord?.id || contactRecord.id,
        [linkedEntity.entityRelationKey]: linkedEntity.entityRecord.id
      })
    })
    this.context.project.endBatch();
    this.close();
  }

  onNameChange({ value }) {
    const { bbar } = this.widgetMap;
    bbar.widgetMap.addButton.disabled = !value;
  }

  onClickLinkedEntity({ record }) {
    this.close();
    switch (record.type) {
      case "Task":
        this.context.entityManager.openEntityManager({ entityType: ENTITY_TYPES.TASK, entityId: record.entityRecord.id });
        break;
      case "Resource":
        this.context.editorHandler.openEditor(EDITOR_KEYS.resourceEditorV2, record.entityRecord, { resource: record.entityRecord })
        break;
      case "Client":
        this.context.editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, record.entityRecord, { client: record.entityRecord })
        break;
      case "User":
        this.context.editorHandler.openEditor(EDITOR_KEYS.userEditor, record.entityRecord, { user: record.entityRecord })
        break;
    }
  }

  // Bug: If the entity finder is already opened from Task Finder icon on header and then user attempts to open the entity finder from contact linked to field then the old entity finder will appear that user opened from the app header
  onAssociatedEntitiesTrigger() {
    this.context.editorHandler.openEditor(EDITOR_KEYS.entityFinder, null, {
      onEntitiesSelected: this.onEntitiesSelected.bind(this)
    }, {
      by: "recordType",
      useExisting: true
    })
  }

  onEntitiesSelected(selectedEntities) {
    const { associatedEntitiesField } = this.widgetMap;
    const entitiesToBeLinked = associatedEntitiesField.items.map((entity) => ({
      id: entity.id,
      name: entity.name,
      type: entity.type,
      entityRecord: entity.entityRecord,
      entityContactStoreKey: entity.entityContactStoreKey,
      entityRelationKey: entity.entityRelationKey
    }));

    Object.keys(selectedEntities).forEach((entityStoreName) => {
      let entityNamePrefix = "";
      selectedEntities[entityStoreName].forEach((entityRecord) => {
        const isEntityAssociated = associatedEntitiesField.items.find((record) => record.id === `${entityRecord.entityVisibleName}-${entityRecord.id}`);
        if (!isEntityAssociated) {
          let entityRelationKey = null;
          let entityContactStoreKey = null;
          const entityType = entityRecord.entityVisibleName;
          if (entityStoreName === STORE_KEYS.taskStore) {
            entityRelationKey = "taskId";
            entityContactStoreKey = STORE_KEYS.taskContactStore;
          }
          else if (entityStoreName === STORE_KEYS.resourceStore) {
            entityRelationKey = "resourceId";
            entityContactStoreKey = STORE_KEYS.resourceContactStore;
          }
          else if (entityStoreName === STORE_KEYS.clientStore) {
            entityRelationKey = "clientId";
            entityContactStoreKey = STORE_KEYS.clientContactStore;
          }
          else if (entityStoreName === STORE_KEYS.boardUserStore) {
            entityRelationKey = "userId";
            entityContactStoreKey = STORE_KEYS.userContactStore;
            entityNamePrefix = "Board User: ";
          }
          const newLinkedEntity = {
            id: `${entityType}-${entityRecord.id}`,
            name: entityNamePrefix + entityRecord.name,
            type: entityType,
            entityRecord,
            entityContactStoreKey,
            entityRelationKey
          };
          const isOldLinkedEntity = this.linkedEntitiesData.removed.find((linkedEntity) => linkedEntity.id === newLinkedEntity.id);
          if (!isOldLinkedEntity) {
            this.linkedEntitiesData.added.push(newLinkedEntity);
          } else {
            this.linkedEntitiesData = {
              ...this.linkedEntitiesData,
              removed: this.linkedEntitiesData.removed.filter((linkedEntity) => linkedEntity.id !== newLinkedEntity.id)
            }
          }
          entitiesToBeLinked.push(newLinkedEntity)
        }
      })
    })
    associatedEntitiesField.items = entitiesToBeLinked;
    associatedEntitiesField.value = entitiesToBeLinked.map((entity) => entity.id);
  }
}

SwivuContactFinder.initClass()

const SwivuContactFinderPopup = ({ context, record, onClose, isShow, editorInstance }) => {
  const onShowHandler = useCallback((editor, otherParams = {}) => {
    const { contactRecord, selectedEntities, clientRecord } = record || {};
    editor.project = context.project;
    editor.loadRecord(contactRecord, context, { ...otherParams, selectedEntities, clientRecord });
    editorInstance.editorRef = editor;
  }, [context, editorInstance, record]);

  return (
    <SwivuModal
      widgetType={WIDGET_KEYS.swivuContactFinderPopup}
      isShow={isShow}
      onHide={onClose}
      onShow={onShowHandler}
    />
  )
};

export default SwivuContactFinderPopup;
